import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import logo from "../assets/images/truviq-logo-1.png";
import { Link } from "react-router-dom";
const NAV_ITEMS = [
  {
    label: "Industries",
    href: "/",
    columns: [
      {
        title: "Healthcare",
        items: [
          {
            label: "Prior Authorization Automation",
            href: "/industries/healthcare/prior-auth",
          },
          {
            label: "Claims Orchestration & Denial Management",
            href: "/industries/healthcare/claims",
          },
          {
            label: "Patient Intake & Registration",
            href: "/industries/healthcare/intake",
          },
          {
            label: "Referral Management & Care Coordination",
            href: "/industries/healthcare/referrals",
          },
          {
            label: "Revenue Cycle Automation",
            href: "/industries/healthcare/revenue-cycle",
          },
          {
            label: "EHDS-Ready Health Data Exchange",
            href: "/industries/healthcare/ehds",
          },
        ],
      },
      {
        title: "Banking & Financial Services",
        items: [
          {
            label: "KYC & Customer Onboarding Automation",
            href: "/industries/banking/kyc",
          },
          {
            label: "Loan Origination Orchestration",
            href: "/industries/banking/loan-origination",
          },
          {
            label: "Fraud & Dispute Case Management",
            href: "/industries/banking/fraud",
          },
          {
            label: "Regulatory Compliance Workflow Automation",
            href: "/industries/banking/compliance",
          },
        ],
      },
      {
        title: "Public Sector & Government",
        items: [
          {
            label: "Citizen Service Orchestration",
            href: "/industries/public-sector/citizen-services",
          },
          {
            label: "Benefits & Case Management Automation",
            href: "/industries/public-sector/benefits",
          },
          {
            label: "Government Workflow Digitization",
            href: "/industries/public-sector/workflow-digitization",
          },
        ],
      },
    ],
  },
  {
    label: "Capabilities",
    href: "/",
    columns: [
      {
        title: "Agentic Orchestration & Workflow Automation",
        items: [
          {
            label: "Camunda Implementation & Managed Services",
            href: "/capabilities/camunda",
          },
          {
            label: "Pega Application Development & Support",
            href: "/",
          },
        ],
      },
      {
        title: "AI Engineering",
        items: [
          {
            label: "AI Governance",
            href: "/",
          },
          { label: "RAG Agents", href: "/" },
          {
            label: "Multi-Agent Systems",
            href: "/",
          },
          { label: "Red Teaming", href: "/" },
          { label: "AI Value Strategy", href: "/" },
        ],
      },
      {
        title: "One orchestration layer",
        items: [
          {
            label: `
              Every capability ties back to the same discipline: coordinating decisions, systems, and people around a process that has to be right.
              `,
            href: "",
          },
          {
            label: "See our AI approach",
          },
        ],
      },
    ],
  },
  {
    label: "AI at Truviq",
    href: "/",
  },
  {
    label: "Innovations",
    href: "/",
    columns: [
      {
        title: "Built by us",
        items: [
          { label: "Orkest AI", href: "/" },
          { label: "Pega Control Tower", href: "/" },
          { label: "Red Teaming", href: "/" },
        ],
      },
      {
        title: "Not client deliverables — products",
        items: [
          {
            label: `
              Tools we built for our own delivery first, then packaged for clients: an orchestration accelerator, a Pega environment dashboard, and AI red-teaming for agentic systems.
            `,
            href: "/",
          },
        ],
      },
    ],
  },
  {
    label: "Partners",
    href: "/",
    columns: [
      {
        title: "Technology partners",
        items: [
          { label: "Camunda", href: "/" },
          { label: "Pega", href: "/" },
          { label: "AntWork", href: "/" },
          { label: "ServiceNow (AI Control Tower)", href: "/" },
        ],
      },
      {
        title: "Certified, not just familiar",
        items: [
          {
            label: `
              Each partner page lists our current tier and the joint use cases we've delivered.
            `,
            href: "/",
          },
        ],
      },
    ],
  },
  {
    label: "Global Partners",
    href: "/",
    columns: [
      {
        title: "Where we operate",
        items: [
          {
            label: `
             Overview — US, EU & India
            `,
            href: "/",
          },
          {
            label: `
             United States
            `,
            href: "/",
          },
        ],
      },
      {
        items: [
          {
            label: `
             Europe (Netherlands)
            `,
            href: "/",
          },
          {
            label: `
             India Delivery Center
            `,
            href: "/",
          },
        ],
      },
    ],
  },
  {
    label: "Company",
    href: "/",
    columns: [
      {
        title: "Insights",
        items: [
          { label: "Blog", href: "/blogs" },
          { label: "Case Studies", href: "/case-studies" },
          { label: "Events", href: "/events" },
        ],
      },
      {
        title: "About",
        items: [
          { label: "Our Story", href: "/about" },
          { label: "Leadership", href: "/about" },
          { label: "Careers", href: "/about" },
        ],
      },
    ],
  },
];

export default function MegaNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (label) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    const item = NAV_ITEMS.find((n) => n.label === label);
    if (item?.columns?.length) {
      setActiveDropdown(label);
    } else {
      setActiveDropdown(null);
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 180);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeItem = NAV_ITEMS.find((item) => item.label === activeDropdown);

  return (
    <header
      ref={navRef}
      className="fixed top-0 inset-x-0 w-full z-50 pointer-events-none flex flex-col items-center pt-2 sm:pt-4"
    >
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        className={`pointer-events-auto transition-all duration-300 flex items-center justify-between ${
          scrolled
            ? "3xl:w-4/5 w-11/12 rounded-full px-6 bg-[#f7f7f5]/20 backdrop-blur-3xl border border-black/10 shadow-[0_12px_35px_rgba(0,0,0,0.06)] py-2.5 sm:px-8"
            : "3xl:w-[90%] w-full rounded-none bg-transparent border-b border-black/[0.05] py-4 px-6 lg:px-12"
        }`}
      >
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src={logo}
            alt="Truviq Logo"
            className={`w-auto object-contain transition-all duration-300 ${
              scrolled ? "h-9 sm:h-11" : "h-12 sm:h-14"
            }`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV_ITEMS.map((item) => {
            const hasChildren = Boolean(item.columns?.length);
            const isOpen = activeDropdown === item.label;

            if (!hasChildren) {
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className="px-4 py-2 rounded-full text-base font-semibold tracking-tight
             text-slate-800 hover:text-white
             
             bg-[linear-gradient(135deg,#62d4f7_0%,#5dc9f0_40%,#3579be_100%)]
             transition-all duration-200"
                >
                  {item.label}
                </Link>
              );
            }

            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  onClick={() => setActiveDropdown(isOpen ? null : item.label)}
                  className={`group flex items-center gap-1.5 px-4 py-2 rounded-full text-base font-semibold tracking-tight transition-all duration-200 cursor-pointer ${
                    isOpen
                      ? "bg-[#000036] text-white shadow-sm"
                      : "text-slate-800 hover:text-black hover:bg-[#000036]"
                  }`}
                >
                  <span>{item.label}</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-300 ${
                      isOpen
                        ? "rotate-180 text-white"
                        : "text-slate-400 group-hover:text-[#000036]"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <Link
            to="/contact"
            className="group relative inline-flex items-center justify-between rounded-full gap-3 px-6 py-1.5 border border-black/15 bg-[#000036] text-white font-mono text-xs font-semibold capitalize tracking-widest transition-all duration-300 hover:bg-white hover:text-slate-700 hover:shadow-lg"
          >
            <span>Let's Talk</span>
            <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5 group-hover:bg-slate-700 group-hover:text-white">
              <ArrowUpRight size={15} />
            </div>
          </Link>
        </div>
        <button
          className="xl:hidden p-2.5 rounded-xl text-slate-900 hover:bg-black/5 transition-all pointer-events-auto"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={26} />
        </button>
      </motion.div>

      <div className="w-full max-w-5xl px-6 lg:px-12 pointer-events-none relative">
        <AnimatePresence>
          {activeDropdown && activeItem?.columns?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.99 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => handleMouseEnter(activeDropdown)}
              onMouseLeave={handleMouseLeave}
              className="pointer-events-auto relative mt-2 pt-2"
            >
              <div className="absolute -top-4 inset-x-0 h-6 bg-transparent" />
              <div className="overflow-hidden rounded-3xl bg-white/95 backdrop-blur-2xl border border-black/[0.08] shadow-[0_24px_60px_-15px_rgba(0,0,0,0.12)] p-8">
                <div
                  className={`grid grid-cols-1 ${
                    activeItem.columns.length > 2
                      ? "md:grid-cols-3"
                      : "md:grid-cols-2"
                  } gap-8`}
                >
                  {" "}
                  {activeItem.columns.map((col) => (
                    <div key={col.title} className="flex flex-col">
                      <div className="pb-3 mb-4 border-b border-slate-100">
                        <span className="text-sm font-bold text-[#000036]">
                          {col.title}
                        </span>
                      </div>
                      <div className="flex flex-col space-y-3">
                        {col.items?.map((sub) => (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            onClick={() => setActiveDropdown(null)}
                            className="text-sm text-slate-600 hover:text-black transition-colors duration-150 leading-snug"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-400">
                  <span>VERIFIED ENTERPRISE SYSTEMS ARCHITECTURE</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 220 }}
            className="fixed inset-0 z-[60] bg-[#f7f7f5] h-screen flex flex-col p-6 overflow-y-auto xl:hidden pointer-events-auto"
          >
            <div className="flex items-center justify-between pb-5 border-b border-black/10">
              <img
                src={logo}
                alt="Truviq Logo"
                className="h-12 w-auto object-contain"
              />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2 text-slate-900 rounded-xl hover:bg-black/5"
              >
                <X size={26} />
              </button>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              {NAV_ITEMS.map((item) => {
                const hasChildren = Boolean(item.columns?.length);
                const isExpanded = mobileExpanded === item.label;

                if (!hasChildren) {
                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between p-4 rounded-2xl text-base font-bold text-slate-950 bg-white border border-black/5 shadow-xs"
                    >
                      <span>{item.label}</span>
                    </Link>
                  );
                }

                return (
                  <div key={item.label} className="flex flex-col">
                    <button
                      onClick={() =>
                        setMobileExpanded(isExpanded ? null : item.label)
                      }
                      className="flex items-center justify-between p-4 rounded-2xl text-base font-bold text-slate-950 bg-white border border-black/5 shadow-xs"
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-300 text-slate-500 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="flex flex-col gap-4 pl-3 pr-2 py-4 overflow-hidden bg-white/60 rounded-2xl mt-1.5 border border-black/5"
                        >
                          {item.columns?.map((col) => (
                            <div
                              key={col.title}
                              className="flex flex-col gap-2"
                            >
                              <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                                {col.title}
                              </span>
                              <div className="flex flex-col gap-2.5 pl-2 border-l border-slate-200">
                                {col.items?.map((sub) => (
                                  <Link
                                    key={sub.label}
                                    to={sub.href}
                                    onClick={() => setOpen(false)}
                                    className="text-xs text-slate-600 hover:text-black py-0.5 leading-snug"
                                  >
                                    {sub.label}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex justify-center items-center gap-2 rounded-2xl bg-slate-950 text-white text-sm font-mono uppercase tracking-widest py-4 font-bold shadow-md"
              >
                <span>Let's Talk</span>
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
